import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCounts } from '@app/src/api/fetchHooks'
import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { FilterOption, MrtCountTarget } from '../mrtCollectionTypes'
import { groupsWithProviderPath } from '../withProviderPath'
import { ONE_MINUTE_MS } from '@app/src/wf-constants'

export type ProviderCountSelection = { key: string; payload: FilterGroup[] }

/** The providers themselves, counted on the providers endpoint. */
export const PROVIDER_COUNT_TARGET: MrtCountTarget = {
  endpoint: endpoints.providerCount,
  fetchKey: FetchKey.ProviderCount,
  path: '',
}

/**
 * How many rows each selection matches, by its key: one count per selection, for the values a facet can't
 * count. The selections are written on the provider and sent through the target's path, so a table with
 * a count endpoint of its own counts its own rows. The table's other filters narrow every count, as they
 * narrow the options of every other filter.
 */
const useProviderCounts = (
  selections: ProviderCountSelection[],
  otherFilterGroups: FilterGroup[],
  target: MrtCountTarget = PROVIDER_COUNT_TARGET,
): { countByKey: Map<string, number>; isLoading: boolean } => {
  // A failed count only leaves the options without numbers; the filter itself still works.
  const { counts, isLoading } = useFetchCounts(
    selections.map(({ payload }) => ({
      key: target.fetchKey,
      endpoint: target.endpoint,
      payload: [...otherFilterGroups, ...groupsWithProviderPath(target.path, payload)],
      options: { staleTime: ONE_MINUTE_MS },
    })),
    { shouldShowErrorNotification: false },
  )
  const countByKey = new Map(
    selections.flatMap(({ key }, index) => {
      const count = counts[index]
      return count === undefined ? [] : [[key, count] as const]
    }),
  )
  return { countByKey, isLoading }
}

/** The options with each one's count, where it has one. */
export const withCounts = (options: FilterOption[], countByKey: Map<string, number>): FilterOption[] =>
  options.map(option => {
    const count = countByKey.get(option.value)
    return count === undefined ? option : { ...option, count }
  })

export default useProviderCounts
